export interface OCRResult {
  success: boolean;
  data?: {
    title?: string;
    amount?: number;
    currency?: string;
    date?: string;
    merchant?: string;
    category?: string;
    confidence?: number;
  };
  error?: string;
}

interface ImageFileInfo {
  file: File;
  name: string;
  type: string;
  size: number;
}

function findEnv(key: string): string | undefined {
  try {
    // Common variants used by different bundlers
    const candidates = [
      // direct
      (process as any)?.env?.[key],
      // CRA
      (process as any)?.env?.[`REACT_APP_${key}`],
      // Vite
      (process as any)?.env?.[`VITE_${key}`],
      // import.meta.env (Vite)
      (typeof import.meta !== 'undefined' ? (import.meta as any).env?.[key] : undefined),
      (typeof import.meta !== 'undefined' ? (import.meta as any).env?.[`VITE_${key}`] : undefined),
      // window globals
      (window as any)?.[key],
      (window as any)?.__ENV__?.[key],
      // older script writes into window.__CUBEMEET_ENV__ via apps/web/scripts/generate-env.cjs
      (window as any)?.__CUBEMEET_ENV__?.[key],
      (window as any)?.__CUBEMEET_ENV__?.[`VITE_${key}`],
      (window as any)?.APP_ENV?.[key],
    ];

    for (const c of candidates) {
      if (typeof c === 'string' && c.trim()) return c.trim();
    }
  } catch (e) {
    // ignore
  }
  return undefined;
}

const WORKERS_URL = findEnv('ZHIPU_AI_WORKERS_URL') || '';
const WORKERS_TOKEN = findEnv('ZHIPU_AI_WORKERS_TOKEN') || '';

function buildOCRPrompt(): string {
  return `请分析这张收据图片，提取信息并严格按照以下JSON格式返回，不要添加任何其他文字：

{
  "title": "收据标题或商品描述",
  "amount": 123.45,
  "currency": "CNY",
  "date": "2024-01-01",
  "merchant": "商家名称",
  "category": "food",
  "confidence": 0.85
}

分类选项：transport, accommodation, food, flight, taxi, attractions, shopping, entertainment, insurance, visa, communication, general

要求：
1. 只返回纯JSON，不要markdown格式
2. 金额必须是数字类型
3. 日期格式：YYYY-MM-DD
4. 无法识别的字段设为null
5. 置信度范围：0-1
6. 货币字段必须使用ISO 4217三字母代码（例如 CNY, HKD, USD）。如果收据上只有符号（例如 "$"），请尝试根据商家名称、收据上的地址或语言/上下文推断最可能的货币；如果无法确定，请返回null而不是返回符号或错误的代码。

7. 对于返回的 "title" 字段：如果能够识别出商家名称（merchant），请直接把 merchant 的值作为 title（即 title 应与 merchant 相同）；仅当 merchant 不可用时，才返回收据上的商品/服务简短描述作为 title。绝对不要把电话号码或纯数字串作为 title；如果候选文本像电话号码，请返回 null 或使用 merchant 替代。

请严格按照格式返回，不要添加任何解释文字。`;
}

async function fileToBase64(file: File): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = reader.result as string;
      const base64Data = res.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = (e) => reject(e);
    reader.readAsDataURL(file);
  });
}

export async function recognizeReceipt(image: ImageFileInfo): Promise<OCRResult> {
  if (!WORKERS_URL || !WORKERS_TOKEN) {
    console.warn('Workers URL or token not configured for web OCR. Checked process.env, import.meta.env, and window globals.');
    return { success: false, error: 'WORKERS_NOT_CONFIGURED' };
  }

  try {
    const base64Image = await fileToBase64(image.file);

    const requestBody = {
      messages: [
        {
          role: 'user',
          content: [
            { type: 'text', text: buildOCRPrompt() },
            { type: 'image_url', image_url: { url: `data:${image.type};base64,${base64Image}` } },
          ],
        },
      ],
      max_tokens: 1000,
      temperature: 0.1,
      request_type: 'ocr',
    };

    const resp = await fetch(WORKERS_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${WORKERS_TOKEN}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Workers request failed: ${resp.status} ${resp.statusText} ${text}`);
    }

    const result = await resp.json();
    const content = (result as any).choices?.[0]?.message?.content;
    if (!content) throw new Error('Invalid API response');

    // Extract JSON from content
    let jsonContent = content;
    const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/);
    if (jsonMatch) jsonContent = jsonMatch[1];
    else if (content.includes('{') && content.includes('}')) {
      const start = content.indexOf('{');
      const end = content.lastIndexOf('}') + 1;
      jsonContent = content.substring(start, end);
    }

    let ocrData: any;
    try {
      ocrData = JSON.parse(jsonContent);
    } catch (err) {
      // fallback: try to extract numbers/dates heuristically
      console.warn('JSON parse failed, fallback manual extract', err);
      const amountMatch = content.match(/(\d+\.?\d*)/);
      const dateMatch = content.match(/(\d{4}[-/]\d{1,2}[-/]\d{1,2})/);
      ocrData = {};
      if (amountMatch) ocrData.amount = parseFloat(amountMatch[1]);
      if (dateMatch) ocrData.date = dateMatch[1].replace(/\//g, '-');
      ocrData.confidence = 0.3;
    }

    return {
      success: true,
      data: {
        title: ocrData.title || undefined,
        amount: typeof ocrData.amount === 'number' ? ocrData.amount : (typeof ocrData.amount === 'string' && !isNaN(parseFloat(ocrData.amount)) ? parseFloat(ocrData.amount) : undefined),
        currency: ocrData.currency || 'CNY',
        date: ocrData.date || undefined,
        merchant: ocrData.merchant || undefined,
        category: ocrData.category || 'general',
        confidence: typeof ocrData.confidence === 'number' ? Math.max(0, Math.min(1, ocrData.confidence)) : 0.5,
      },
    };
  } catch (error: any) {
    console.error('Web OCR failed:', error);
    return { success: false, error: error?.message || 'OCR failed' };
  }
}

export default { recognizeReceipt };
